import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart, MessageCircle } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getUser, getFollowingUsers, setUserFollow } from "../../entities/user/api";
import { listPosts } from "../../entities/post/api";
import { adaptPost } from "../../entities/post/adapter";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";
import { useAuthStore } from "../../features/auth/store";
import { IS_API_MODE } from "../../shared/config/runtime";
import { getApiErrorMessage } from "../../shared/api/errors";
import defaultAvatar from "../../shared/ui/defaultAvatar";

export default function PublicProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const isOwnProfile = Boolean(currentUser?.id && String(currentUser.id) === String(userId));
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    if (isOwnProfile) navigate("/profile", { replace: true });
  }, [isOwnProfile, navigate]);

  const userQuery = useQuery({
    queryKey: ["profile", userId],
    queryFn: () => getUser(userId),
    enabled: IS_API_MODE && Boolean(userId) && !isOwnProfile,
  });
  const followingQuery = useQuery({
    queryKey: ["users", currentUser?.id, "following"],
    queryFn: () => getFollowingUsers(currentUser.id),
    enabled: IS_API_MODE && Boolean(currentUser?.id) && !isOwnProfile,
  });
  const postsQuery = useQuery({
    queryKey: ["posts", "public-profile", userId],
    queryFn: () => listPosts({ pageSize: 100 }),
    enabled: IS_API_MODE && Boolean(userId) && !isOwnProfile,
  });
  const profile = userQuery.data;
  const publicPosts = useMemo(() => {
    const data = Array.isArray(postsQuery.data)
      ? postsQuery.data
      : postsQuery.data?.results || postsQuery.data?.items || [];
    return data
      .filter((post) => String(post.author?.id || post.author_id) === String(userId))
      .map(adaptPost);
  }, [postsQuery.data, userId]);
  const followingItems = followingQuery.data?.results || followingQuery.data || [];

  useEffect(() => {
    if (!profile?.id || !followingQuery.data) return;
    setFollowing(followingItems.some((item) => String(item.id) === String(profile.id)));
  }, [followingItems, followingQuery.data, profile?.id]);

  const followMutation = useMutation({
    mutationFn: (nextFollowing) => setUserFollow(userId, nextFollowing),
    onMutate: (nextFollowing) => setFollowing(nextFollowing),
    onError: (_, nextFollowing) => setFollowing(!nextFollowing),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users", currentUser?.id, "following"] }),
  });

  if (isOwnProfile) return null;
  if (userQuery.isPending && IS_API_MODE) return <div className="detail-state">正在加载用户资料……</div>;
  if (userQuery.isError && IS_API_MODE) {
    return <div className="detail-state error">{getApiErrorMessage(userQuery.error, "用户资料加载失败")}</div>;
  }
  if (!profile) return <div className="detail-state">用户不存在</div>;

  const avatarUrl = profile.avatar_url || profile.avatarUrl || defaultAvatar;
  const followerCount = profile.follower_count ?? profile.followerCount ?? 0;
  const followingCount = profile.following_count ?? profile.followingCount ?? 0;
  const postCount = profile.post_count ?? profile.postCount ?? publicPosts.length;
  const likedTotal = publicPosts.reduce((total, post) => total + Number(post.likes || 0), 0);

  return (
    <section className="profile-page">
      <div className="profile-content">
        <header className="profile-header">
          <img className="profile-avatar" src={avatarUrl} alt="" />
          <div className="profile-main">
            <div className="profile-title">
              <div>
                <h1>{profile.nickname || profile.username}</h1>
                <span>@{profile.username}</span>
              </div>
              <div className="profile-actions">
                <button className={following ? "secondary-button" : "primary-button"} onClick={() => followMutation.mutate(!following)} disabled={followMutation.isPending}>
                  {followMutation.isPending ? "处理中……" : following ? "已关注" : "关注"}
                </button>
                <button className="secondary-button profile-message-button" type="button" onClick={() => navigate("/messages", { state: { targetUserId: profile.id } })} aria-label="私信">
                  <MessageCircle size={18} />
                  私信
                </button>
              </div>
            </div>
            <p>{profile.signature || "还没有填写个人简介"}</p>
            <div className="profile-stats">
              <span><b>{postCount}</b> 帖子</span>
              <span><b>{followerCount}</b> 粉丝</span>
              <span><b>{followingCount}</b> 关注</span>
              <span><b>{likedTotal}</b> 获赞</span>
            </div>
          </div>
        </header>
        <div className="profile-tabs">
          <button className="active">帖子</button>
          <button onClick={() => navigate("/profile")}>我的主页</button>
        </div>
        {postsQuery.isPending ? <p className="profile-empty">正在加载作品……</p> : null}
        {postsQuery.isError ? <p className="profile-empty">作品加载失败</p> : null}
        <div className="profile-grid">
          {publicPosts.map((post) => (
            <button className="profile-post-tile" key={post.id} onClick={() => navigate(`/posts/${post.id}`, { state: { backgroundLocation: location } })}>
              {post.image ? <img src={post.image} alt={`${post.author}的设计作品`} /> : <PosterArtwork artwork={post.artwork} compact />}
              <span className="profile-post-overlay">
                <span className="profile-like-state"><Heart size={18} /> {new Intl.NumberFormat("zh-CN").format(post.likes || 0)}</span>
              </span>
            </button>
          ))}
        </div>
        {!postsQuery.isPending && !postsQuery.isError && publicPosts.length === 0 ? <p className="profile-empty">还没有发布作品</p> : null}
      </div>
    </section>
  );
}
